import React, { FC, ReactElement, useState, useEffect } from 'react';
import classNames from 'classnames/bind';
import styles from './Pagination.module.scss';

const cn = classNames.bind(styles);

const IconArrow = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    color="#000C37"
  >
    <path d="M14 8L10 12L14 16" stroke="#currentColor" strokeLinecap="round" fill="#curentColor" />
  </svg>
);

interface PaginationProps {
  buttonsQuantity: number;
  setCurrentPage: (state: any) => void;
};

const Pagination: FC<PaginationProps> = ({ buttonsQuantity, setCurrentPage }): ReactElement => {
  const [activeButton, setActiveButton] = useState<number>(1);
  const [visibleButtons, setVisibleButtons] = useState<Array<number | string>>([]);

  const generateButtonElements = (buttonsQuantity: number) => {
    const buttonElements: number[] = [];
    for (let i = 1; i <= buttonsQuantity; i++) {
      buttonElements.push(i);
    }
    return buttonElements;
  };

  const buttonElements = generateButtonElements(buttonsQuantity);

  const renderButtonElements = () => {
    if (!visibleButtons.length) return;

    return visibleButtons.map((item, index) => {
      return (
        <button
          key={index}
          className={cn('pagination-button', { 'active': item === activeButton, 'not-clickable': item === '...' })}
          onClick={() => setActiveButton(Number(item))}
        >
          {item}
        </button>
      );
    });
  };

  const decreaseActiveButtonNumber = () => {
    setActiveButton((prevState) => prevState === 1 ? prevState : prevState - 1);
  };

  const increaseActiveButtonNumber = () => {
    setActiveButton((prevState) => prevState === buttonElements.length ? prevState : prevState + 1);
  };

  useEffect(() => {
    let temporaryButtonElements: Array<number | string> = [...buttonElements];

    if (activeButton >= 1 && activeButton <= 4) {
      temporaryButtonElements = [1, 2, 3, 4, 5, '...', buttonElements.length];
    } else if (activeButton === 4) {
      const sliced = buttonElements.slice(0, 4 + 1);
      temporaryButtonElements = [...sliced, '...', buttonElements.length];
    } else if (activeButton > 4 && activeButton < buttonElements.length - 2) {
      const sliced01 = buttonElements.slice(activeButton - 2, activeButton);
      const sliced02 = buttonElements.slice(activeButton, activeButton + 1);
      temporaryButtonElements = ([1, '...', ...sliced01, ...sliced02, '...', buttonElements.length]);
    } else if (activeButton > buttonElements.length - 3) {
      const sliced = buttonElements.slice(buttonElements.length - (3 + 1));
      temporaryButtonElements = ([1, '...', ...sliced]);
    }

    setVisibleButtons(temporaryButtonElements);
    setCurrentPage(activeButton);
  }, [activeButton]);

  return (
    <div className={cn('pagination')}>
      <button
        className={cn('pagination-button', 'arrow', 'left')}
        onClick={decreaseActiveButtonNumber}
      >
        {IconArrow}
      </button>
      <div className={cn('pagination-buttons')}>
        {renderButtonElements()}
      </div>
      <button
        className={cn('pagination-button', 'arrow', 'right')}
        onClick={increaseActiveButtonNumber}
      >
        {IconArrow}
      </button>
    </div>
  );
};

export default Pagination;